import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Calendar, Users, Euro, Home, Tag, Clock, XCircle } from 'lucide-react';
import { API_BASE_URL } from '../../config/api.config';
import './BookingConfirmation.css';
import { gastStatus } from '../../utils/buchungsstatus';
import { gastHinweis } from '../../utils/gasthinweis';

interface Booking {
  id: number;
  bookingNumber: string;
  // Direct fields from API
  tourTypeName?: string;
  scheduleDate?: string;
  startTime?: string;
  bookingDate?: string;
  variant?: string;
  // Alternative nested structure (for backwards compatibility)
  schedule?: {
    date: string;
    time: string;
    tourTypeName: string;
  };
  participantCount?: number;
  numberOfParticipants?: number;
  // Pricing
  pricing?: {
    totalGross?: number;
    totalNet?: number;
    depositAmount?: number;
  };
  totalPrice?: number;
  paymentAmount?: number;
  paymentMethod?: string;
  // Customer
  customer?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  customerName?: string;
  customerEmail?: string;
  // Payment
  payment?: {
    totalPaid?: number;
    method?: string;
    // T-088 (v1.8.20): PaymentSummaryDto liefert amountPaid/amountDue (inkl. Alt-Anzahlung) und die Altfall-Felder
    amountPaid?: number;
    amountDue?: number;
    isLegacyImport?: boolean;
    legacyDepositAmount?: number | null;
    legacyDepositPaidAt?: string | null; // yyyy-MM-dd
    legacyInvoiceNumber?: string | null;
  };
  // T-130 (v1.8.38): API liefert { status, statusDisplayName, ... } (BookingStatusDto); alte Form code/displayName bleibt lesbar
  status?: {
    status?: string;
    statusDisplayName?: string;
    code?: string;
    displayName?: string;
  } | string;
  // T-137 (v1.8.39): Hinweise zur Buchung (z. B. "bringt Kinderwagen mit")
  specialRequests?: string | null;
  // T-139 (v1.8.39): Gültigkeit des Angebots
  quoteValidUntil?: string | null;
  // Voucher redemption info (from API)
  appliedVoucher?: {
    code: string;
    amountRedeemed: number;
    remainingAfterRedemption: number;
  };
}

const BookingConfirmation: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);

  // Get voucher data from URL params (passed from booking success) or from API response
  const urlVoucherCode = searchParams.get('voucherCode');
  const urlVoucherDiscount = parseFloat(searchParams.get('voucherDiscount') || '0');

  useEffect(() => {
    loadBooking();
  }, [bookingId]);

  const loadBooking = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${API_BASE_URL}/bookings/${bookingId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setBooking(data.data || data);
      }
    } catch (error) {
      console.error('Error loading booking:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="booking-loading">
        <div className="spinner"></div>
        <p>Buchung wird geladen...</p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="booking-error">
        <h2>Buchung nicht gefunden</h2>
        <p>Die angeforderte Buchung konnte nicht gefunden werden.</p>
        <button onClick={() => navigate('/dashboard')}>
          Zurück zum Dashboard
        </button>
      </div>
    );
  }

  // Extract data flexibly from different possible structures
  const tourTypeName = booking.tourTypeName || booking.schedule?.tourTypeName || booking.variant || '-';
  const scheduleDate = booking.scheduleDate || booking.schedule?.date || booking.bookingDate || '-';
  const startTime = booking.startTime || booking.schedule?.time || '-';
  const participantCount = booking.participantCount || booking.numberOfParticipants || 0;
  const subtotal = booking.pricing?.totalGross || booking.totalPrice || 0;

  // Get voucher data: prefer API data, fallback to URL params (for fresh bookings)
  const voucherCode = booking.appliedVoucher?.code || urlVoucherCode;
  const voucherDiscount = booking.appliedVoucher?.amountRedeemed || urlVoucherDiscount;

  // Apply voucher discount if present
  const totalPrice = voucherDiscount > 0 ? Math.max(0, subtotal - voucherDiscount) : subtotal;
  // T-088 (v1.8.20): amountPaid aus der PaymentSummary (enthält die Alt-Anzahlung der Altfall-Übernahme)
  const paymentAmount = booking.payment?.amountPaid || booking.payment?.totalPaid || booking.paymentAmount || booking.pricing?.depositAmount || 0;
  const legacyDeposit = booking.payment?.legacyDepositAmount ?? 0;
  const formatDateDe = (isoDate?: string | null) => {
    const m = isoDate ? /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate) : null;
    return m ? `${m[3]}.${m[2]}.${m[1]}` : (isoDate || '');
  };
  const customerEmail = booking.customer?.email || booking.customerEmail || '-';
  // T-130 (v1.8.38): Status aus dem API-Code (bisher immer 'Bestätigt', auch bei offenen Angeboten)
  const gs = gastStatus(booking.status);
  const statusDisplay = gs.text;
  const istVerbindlich = gs.verbindlich;
  const istAngebot = gs.art === 'angebot' || gs.art === 'angebotUngueltig';
  const istRot = gs.art === 'storniert' || gs.art === 'angebotUngueltig' || gs.art === 'nichtErschienen';
  const statusFarbe = istVerbindlich ? '#10b981' : istRot ? '#dc2626' : '#d97706';
  // T-139 (v1.8.39): offenes Angebot direkt aus dem Kundenkonto annehmen (Annahmeseite, angemeldeter Besitzer braucht kein Link-Token)
  // Review v1.8.39 Befund 8: abgelaufene Angebote (Gültigkeit vorbei, Status noch OfferSent) nicht mehr zum Annehmen anbieten
  const gueltigBis = booking.quoteValidUntil ? new Date(booking.quoteValidUntil) : null;
  const angebotAbgelaufen = gs.code === 'OfferSent' && !!gueltigBis && !isNaN(gueltigBis.getTime()) && gueltigBis.getTime() < Date.now();
  const kannAnnehmen = gs.code === 'OfferSent' && !angebotAbgelaufen;
  // T-137 (v1.8.39): Hinweise ohne automatischen Anlagevermerk
  const hinweisText = gastHinweis(booking.specialRequests);

  // Format date for German locale
  const formatDate = (dateStr: string) => {
    if (!dateStr || dateStr === '-') return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('de-DE', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Format time
  const formatTime = (timeStr: string | undefined) => {
    if (!timeStr || timeStr === '-') return '-';
    // Handle TimeSpan format "HH:mm:ss"
    const parts = timeStr.toString().split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]} Uhr`;
    }
    return timeStr;
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  return (
    <div className="booking-confirmation-container">
      {/* Success Header */}
      <div className="booking-confirmation-header">
        {istVerbindlich ? (
          <CheckCircle className="success-icon" />
        ) : istRot ? (
          <XCircle className="success-icon" style={{ background: '#fee2e2', color: '#dc2626' }} />
        ) : (
          <Clock className="success-icon" style={{ background: '#fef3c7', color: '#d97706' }} />
        )}
        <h1>{istAngebot ? 'Angebotsdetails' : 'Buchungsdetails'}</h1>
        <p className="status-text">
          Status: <strong style={{ color: statusFarbe }}>{statusDisplay}</strong>
          {istVerbindlich && customerEmail !== '-' && `, Bestätigung an ${customerEmail}`}
        </p>
        {gs.hinweis && (
          <p className="status-hinweis" style={{
            margin: '12px auto 0',
            maxWidth: '560px',
            padding: '10px 14px',
            background: '#fffbeb',
            border: '1px solid #f59e0b',
            borderRadius: '6px',
            color: '#92400e',
            fontSize: '14px'
          }}>
            {gs.hinweis}
          </p>
        )}
        {angebotAbgelaufen && (
          <p className="status-hinweis" style={{ margin: '12px auto 0', maxWidth: '560px', color: '#dc2626', fontSize: '14px' }}>
            Dieses Angebot ist am {gueltigBis!.toLocaleDateString('de-DE')} abgelaufen. Bei Interesse melden Sie sich bitte bei uns.
          </p>
        )}
        {kannAnnehmen && (
          <button
            type="button"
            className="btn-primary"
            style={{ marginTop: '16px' }}
            onClick={() => navigate(`/angebot-annehmen/${booking.id}`)}
          >
            Angebot ansehen und annehmen
          </button>
        )}
      </div>

      {/* Booking Number */}
      <div className="booking-number-card">
        <p className="label">Buchungsnummer</p>
        <p className="number">{booking.bookingNumber}</p>
      </div>

      {/* Booking Details */}
      <div className="booking-details-card">
        <div className="booking-details-section">
          <div className="section-header">
            <Calendar className="icon" />
            <h2>Termindetails</h2>
          </div>
          <div className="detail-row">
            <span className="label">Tour</span>
            <span className="value">{tourTypeName}</span>
          </div>
          <div className="detail-row">
            <span className="label">Datum</span>
            <span className="value">{formatDate(scheduleDate)}</span>
          </div>
          <div className="detail-row">
            <span className="label">Uhrzeit</span>
            <span className="value">{formatTime(startTime)}</span>
          </div>
        </div>

        <div className="booking-details-section">
          <div className="section-header">
            <Users className="icon" />
            <h2>Teilnehmer</h2>
          </div>
          <div className="detail-row">
            <span className="label">Anzahl</span>
            <span className="value participants-count">{participantCount} Personen</span>
          </div>
        </div>

        {hinweisText && (
          <div className="booking-details-section">
            <div className="section-header">
              <Tag className="icon" />
              <h2>Ihre Hinweise</h2>
            </div>
            <div className="detail-row">
              <span className="value" style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>{hinweisText}</span>
            </div>
          </div>
        )}

        <div className="booking-details-section">
          <div className="section-header">
            <Euro className="icon" />
            <h2>Preisübersicht</h2>
          </div>
          {voucherDiscount > 0 && (
            <>
              <div className="detail-row">
                <span className="label">Zwischensumme</span>
                <span className="value">{formatCurrency(subtotal)}</span>
              </div>
              <div className="detail-row voucher">
                <span className="label">
                  <Tag size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Gutschein ({voucherCode})
                </span>
                <span className="value voucher-discount">-{formatCurrency(voucherDiscount)}</span>
              </div>
            </>
          )}
          <div className="detail-row highlight">
            <span className="label">Gesamtpreis</span>
            <span className="value">{formatCurrency(totalPrice)}</span>
          </div>
          {paymentAmount > 0 && (
            <div className="detail-row">
              <span className="label">Bereits bezahlt</span>
              <span className="value">{formatCurrency(paymentAmount)}</span>
            </div>
          )}
          {/* T-088 (v1.8.20): Alt-Anzahlung aus der Altfall-Übernahme ausweisen */}
          {legacyDeposit > 0 && (
            <div className="detail-row sub-info">
              <span className="label">
                davon Anzahlung{booking.payment?.legacyInvoiceNumber ? ` laut Rechnung ${booking.payment.legacyInvoiceNumber}` : ''}
                {booking.payment?.legacyDepositPaidAt ? ` vom ${formatDateDe(booking.payment.legacyDepositPaidAt)}` : ''} (Altverfahren)
              </span>
              <span className="value">{formatCurrency(legacyDeposit)}</span>
            </div>
          )}
          {paymentAmount > 0 && totalPrice > paymentAmount && (
            <div className="detail-row sub-info">
              <span className="label">Restbetrag</span>
              <span className="value">{formatCurrency(totalPrice - paymentAmount)}</span>
            </div>
          )}
          {/* Hinweis zu Zahlungsart: Kurzfristig (< 8 Wochen) = Vollzahlung */}
          {(() => {
            // T-130 (v1.8.38): Zahlungshinweis nur, solange eine Zahlung offen sein kann (nicht bei Angebot, Option, Anfrage,
            // Storno, bezahlten oder vergangenen Fahrten; Review v1.8.38 Befund 4)
            if (!gs.zahlungOffen) return null;
            if (!scheduleDate || scheduleDate === '-') return null;
            try {
              const tripDate = new Date(scheduleDate);
              const now = new Date();
              const weeksUntilTrip = Math.floor((tripDate.getTime() - now.getTime()) / (7 * 24 * 60 * 60 * 1000));
              const isShortNotice = weeksUntilTrip < 8;

              // T-088 (v1.8.20): Altfall mit bereits geleisteter Anzahlung: keine 20-Prozent-Anzahlung mehr ausweisen
              if (legacyDeposit > 0 && !isShortNotice) {
                return (
                  <div className="detail-row" style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    background: '#f0fdf4',
                    borderRadius: '6px',
                    border: '1px solid #16a34a'
                  }}>
                    <span className="label" style={{ fontWeight: '600' }}>
                      Anzahlung bereits geleistet, Schlussrechnung über den Restbetrag folgt vor der Fahrt
                    </span>
                    <span className="value" style={{ fontWeight: '600' }}>
                      {formatCurrency(Math.max(0, totalPrice - paymentAmount))}
                    </span>
                  </div>
                );
              }
              return (
                <div className="detail-row" style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  background: isShortNotice ? '#fef3c7' : '#f0fdf4',
                  borderRadius: '6px',
                  border: isShortNotice ? '1px solid #f59e0b' : '1px solid #16a34a'
                }}>
                  <span className="label" style={{ fontWeight: '600' }}>
                    {isShortNotice
                      ? 'Kurzfristige Buchung - Vollzahlung fällig'
                      : 'Anzahlung 20%'}
                  </span>
                  <span className="value" style={{ fontWeight: '600' }}>
                    {isShortNotice
                      ? formatCurrency(totalPrice)
                      : formatCurrency(totalPrice * 0.2)}
                  </span>
                </div>
              );
            } catch {
              return null;
            }
          })()}
        </div>
      </div>

      {/* Actions */}
      <div className="booking-actions">
        <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
          <Home className="icon" />
          Zum Dashboard
        </button>
        <button className="btn-primary" onClick={() => navigate('/booking')}>
          Neue Buchung
        </button>
      </div>
    </div>
  );
};

export default BookingConfirmation;
